import { useCallback, useRef, useState } from "react";

import {
  DRAG_AUTO_SHRINK_RATIO,
  HEADER_HEIGHT,
  MIN_WINDOW_SIZE,
  PHONE_BEZEL_WIDTH,
  type ResizeDirection,
} from "../constants";
import type { ImpersonationSessionType } from "../types";

interface UseWindowInteractionsOptions {
  sessionId: string;
  sessionType: ImpersonationSessionType;
  position: { x: number; y: number };
  size: { width: number; height: number };
  updateWindowPosition: (id: string, position: { x: number; y: number }) => void;
  updateWindowSize: (id: string, size: { width: number; height: number }) => void;
}

function getChrome(sessionType: ImpersonationSessionType): { width: number; height: number } {
  if (sessionType === "worker") {
    const bezel = PHONE_BEZEL_WIDTH;
    return { width: bezel * 2, height: bezel * 2 + HEADER_HEIGHT };
  }
  return { width: 0, height: HEADER_HEIGHT };
}

function clampDragPosition(
  sessionType: ImpersonationSessionType,
  proposed: { x: number; y: number },
  size: { width: number; height: number }
): { x: number; y: number } {
  const chrome = getChrome(sessionType);
  const maxX = Math.max(0, window.innerWidth - size.width - chrome.width);
  const maxY = Math.max(0, window.innerHeight - size.height - chrome.height);
  return {
    x: Math.max(0, Math.min(proposed.x, maxX)),
    y: Math.max(0, Math.min(proposed.y, maxY)),
  };
}

/**
 * If the window already fills the viewport on either axis, dragging the title
 * bar would be a no-op under the strict clamp. Snap the window to a smaller
 * centered size so the user can actually move it.
 */
function maybeShrinkForDrag(
  sessionType: ImpersonationSessionType,
  size: { width: number; height: number }
): { size: { width: number; height: number }; shrunk: boolean } {
  const chrome = getChrome(sessionType);
  const maxInnerWidth = Math.max(MIN_WINDOW_SIZE.width, window.innerWidth - chrome.width);
  const maxInnerHeight = Math.max(MIN_WINDOW_SIZE.height, window.innerHeight - chrome.height);

  const fillsHorizontally = size.width >= maxInnerWidth;
  const fillsVertically = size.height >= maxInnerHeight;
  if (!fillsHorizontally && !fillsVertically) {
    return { size, shrunk: false };
  }

  return {
    shrunk: true,
    size: {
      width: fillsHorizontally
        ? Math.max(MIN_WINDOW_SIZE.width, Math.round(maxInnerWidth * DRAG_AUTO_SHRINK_RATIO))
        : size.width,
      height: fillsVertically
        ? Math.max(MIN_WINDOW_SIZE.height, Math.round(maxInnerHeight * DRAG_AUTO_SHRINK_RATIO))
        : size.height,
    },
  };
}

export function useWindowInteractions({
  sessionId,
  sessionType,
  position,
  size,
  updateWindowPosition,
  updateWindowSize,
}: UseWindowInteractionsOptions) {
  const isDragging = useRef(false);
  const isResizing = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const dragSize = useRef(size);
  const resizeStart = useRef({
    pointerX: 0,
    pointerY: 0,
    width: 0,
    height: 0,
    x: 0,
    y: 0,
    direction: "se" as ResizeDirection,
  });
  // Public flag the component reads to render a transparent overlay over the
  // cross-origin iframe during interactions, so pointer events can't get
  // routed into the iframe document and lost.
  const [isInteracting, setIsInteracting] = useState(false);

  const handleDragStart = useCallback(
    (event: React.PointerEvent) => {
      isDragging.current = true;
      setIsInteracting(true);

      const { size: workingSize, shrunk } = maybeShrinkForDrag(sessionType, size);
      dragSize.current = workingSize;
      if (shrunk) {
        updateWindowSize(sessionId, workingSize);
      }

      // Center the shrunk window under the pointer so the title bar follows
      // the cursor naturally; otherwise track relative to where the user
      // grabbed.
      dragOffset.current = shrunk
        ? { x: workingSize.width / 2, y: 0 }
        : { x: event.clientX - position.x, y: event.clientY - position.y };

      if (shrunk) {
        updateWindowPosition(
          sessionId,
          clampDragPosition(
            sessionType,
            { x: event.clientX - dragOffset.current.x, y: event.clientY - dragOffset.current.y },
            workingSize
          )
        );
      }

      (event.target as HTMLElement).setPointerCapture(event.pointerId);
    },
    [position.x, position.y, sessionId, sessionType, size, updateWindowPosition, updateWindowSize]
  );

  const handleDragMove = useCallback(
    (event: React.PointerEvent) => {
      if (!isDragging.current) {
        return;
      }

      const proposed = {
        x: event.clientX - dragOffset.current.x,
        y: event.clientY - dragOffset.current.y,
      };
      updateWindowPosition(sessionId, clampDragPosition(sessionType, proposed, dragSize.current));
    },
    [sessionId, sessionType, updateWindowPosition]
  );

  const handleDragEnd = useCallback(() => {
    isDragging.current = false;
    setIsInteracting(false);
  }, []);

  const handleResizeStart = useCallback(
    (direction: ResizeDirection) => (event: React.PointerEvent) => {
      event.stopPropagation();
      isResizing.current = true;
      setIsInteracting(true);
      resizeStart.current = {
        pointerX: event.clientX,
        pointerY: event.clientY,
        width: size.width,
        height: size.height,
        x: position.x,
        y: position.y,
        direction,
      };
      (event.target as HTMLElement).setPointerCapture(event.pointerId);
    },
    [position.x, position.y, size.width, size.height]
  );

  const handleResizeMove = useCallback(
    (event: React.PointerEvent) => {
      if (!isResizing.current) {
        return;
      }

      const start = resizeStart.current;
      const { direction } = start;
      const deltaX = event.clientX - start.pointerX;
      const deltaY = event.clientY - start.pointerY;
      const chrome = getChrome(sessionType);

      // Compute the prospective new rect, deriving x/y for north/west edges.
      let newWidth = start.width;
      let newHeight = start.height;
      let newX = start.x;
      let newY = start.y;

      if (direction.includes("e")) {
        newWidth = start.width + deltaX;
      }
      if (direction.includes("w")) {
        newWidth = start.width - deltaX;
        newX = start.x + deltaX;
      }
      if (direction.includes("s")) {
        newHeight = start.height + deltaY;
      }
      if (direction.includes("n")) {
        newHeight = start.height - deltaY;
        newY = start.y + deltaY;
      }

      // Enforce minimum size, snapping the origin back if the user crossed it
      // (e.g. dragging the west edge past the original right edge).
      if (newWidth < MIN_WINDOW_SIZE.width) {
        if (direction.includes("w")) {
          newX = start.x + (start.width - MIN_WINDOW_SIZE.width);
        }
        newWidth = MIN_WINDOW_SIZE.width;
      }
      if (newHeight < MIN_WINDOW_SIZE.height) {
        if (direction.includes("n")) {
          newY = start.y + (start.height - MIN_WINDOW_SIZE.height);
        }
        newHeight = MIN_WINDOW_SIZE.height;
      }

      // Clamp to viewport: the outer rect (including chrome) must fit inside
      // window.innerWidth/innerHeight, and the origin must stay non-negative.
      // Re-enforce MIN_WINDOW_SIZE after the negative-origin correction:
      // `newWidth += newX` shrinks the width when the user drags past x=0, and
      // a naive `Math.min(newWidth, max-allowed)` afterwards would happily
      // return that sub-MIN value. Same goes for newY/newHeight.
      if (newX < 0) {
        newWidth += newX;
        newX = 0;
      }
      if (newY < 0) {
        newHeight += newY;
        newY = 0;
      }
      const maxAllowedWidth = Math.max(
        MIN_WINDOW_SIZE.width,
        window.innerWidth - newX - chrome.width
      );
      const maxAllowedHeight = Math.max(
        MIN_WINDOW_SIZE.height,
        window.innerHeight - newY - chrome.height
      );
      newWidth = Math.max(MIN_WINDOW_SIZE.width, Math.min(newWidth, maxAllowedWidth));
      newHeight = Math.max(MIN_WINDOW_SIZE.height, Math.min(newHeight, maxAllowedHeight));

      if (newX !== position.x || newY !== position.y) {
        updateWindowPosition(sessionId, { x: newX, y: newY });
      }
      updateWindowSize(sessionId, { width: newWidth, height: newHeight });
    },
    [sessionId, sessionType, position.x, position.y, updateWindowPosition, updateWindowSize]
  );

  const handleResizeEnd = useCallback(() => {
    isResizing.current = false;
    setIsInteracting(false);
  }, []);

  return {
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    handleResizeStart,
    handleResizeMove,
    handleResizeEnd,
    isInteracting,
  };
}
